import { CheckCircle2, FileX2, Lock, Trash2, XCircle } from "lucide-react";
import React, { useMemo, useState } from "react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatRuDate, formatRub } from "@/lib/format";
import type { IntercompanyCapabilities, ReconResult, ReconRow } from "@/types/api";
import { ApproveBatchDialog, type PendingAction } from "@/pages/intercompany/ApproveBatchDialog";

type RowPhase = "idle" | "done" | "error";
interface RowStatus {
  phase: RowPhase;
  message?: string;
}

const KIND_LABEL: Record<ReconRow["kind"], { label: string; cls: string }> = {
  missing_sf_in_receiver: {
    label: "Нет СФ полученного в БП получателя",
    cls: "bg-amber-100 text-amber-800",
  },
  extra_sf_in_sender: {
    label: "Лишний СФ полученный в БП передатчика",
    cls: "bg-rose-100 text-rose-800",
  },
  ptmo_without_postuplenie: {
    label: "ПТМО без Поступления",
    cls: "bg-slate-100 text-slate-700",
  },
};

function actionAllowed(action: ReconRow["available_action"], caps: IntercompanyCapabilities): boolean {
  if (action === "create_sf") return caps.can_create_sf;
  if (action === "mark_delete") return caps.can_mark_delete;
  return false;
}

function rowToAction(
  row: ReconRow,
  idx: number,
  caps: IntercompanyCapabilities,
): PendingAction | null {
  if (!actionAllowed(row.available_action, caps)) return null;
  if (row.available_action === "create_sf") {
    return {
      rowIndex: idx,
      label: `Создать СФ полученный на Поступление № ${row.postuplenie_number ?? "?"} от ${
        row.postuplenie_date ? formatRuDate(row.postuplenie_date) : "?"
      } (вх. № ${row.sf_issued_number ?? "?"})`,
      req: {
        kind: "create_sf",
        postuplenie_ref_key: row.postuplenie_ref_key,
        sf_issued_ref_key: row.sf_issued_ref_key,
      },
    };
  }
  if (row.available_action === "mark_delete") {
    return {
      rowIndex: idx,
      label: `Пометить на удаление СФ полученный № ${row.sf_received_number ?? "?"} от ${
        row.sf_received_date ? formatRuDate(row.sf_received_date) : "?"
      }`,
      req: { kind: "mark_delete", sf_received_ref_key: row.sf_received_ref_key },
    };
  }
  return null;
}

function ActionCell({
  action, kind, isDone, disabledByCaps, onRun,
}: {
  action: PendingAction | null | undefined;
  kind: ReconRow["available_action"];
  isDone: boolean;
  disabledByCaps: boolean;
  onRun: () => void;
}): React.ReactElement {
  if (isDone) return <span className="text-xs text-emerald-700">выполнено</span>;
  if (action) {
    return (
      <Button size="sm" variant="outline" onClick={onRun}>
        {kind === "create_sf"
          ? <>Создать СФ</>
          : <><Trash2 className="mr-1 h-3.5 w-3.5" />Пометить</>}
      </Button>
    );
  }
  const Icon = disabledByCaps ? Lock : FileX2;
  const text = disabledByCaps ? "действия отключены" : "нет действия";
  return (
    <span className="inline-flex items-center text-xs text-muted-foreground">
      <Icon className="mr-1 h-3.5 w-3.5" />{text}
    </span>
  );
}

export function ReconTable({
  result,
  pairId,
  caps,
}: {
  result: ReconResult;
  pairId: string;
  caps: IntercompanyCapabilities;
}): React.ReactElement {
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [statuses, setStatuses] = useState<Record<number, RowStatus>>({});
  const [pending, setPending] = useState<PendingAction[] | null>(null);

  const readOnly = !caps.can_create_sf && !caps.can_mark_delete;

  // `rowToAction` дорогой(ish) per row × вызывается из нескольких мест — считаем один раз.
  const rowActions = useMemo(
    () => result.rows.map((r, i) => rowToAction(r, i, caps)),
    [result.rows, caps],
  );
  const actionableIdx = useMemo(
    () => rowActions
      .map((a, i) => (a && statuses[i]?.phase !== "done" ? i : -1))
      .filter((i) => i >= 0),
    [rowActions, statuses],
  );

  const allSelected = actionableIdx.length > 0 && actionableIdx.every((i) => selected.has(i));

  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(actionableIdx));
  };
  const toggleOne = (idx: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const handleResult = (rowIndex: number, res: { ok: boolean; message: string }) => {
    setStatuses((prev) => ({
      ...prev,
      [rowIndex]: { phase: res.ok ? "done" : "error", message: res.message },
    }));
    if (res.ok) {
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(rowIndex);
        return next;
      });
    }
  };

  const runSelected = () => {
    const actions = [...selected]
      .sort((a, b) => a - b)
      .map((i) => rowActions[i])
      .filter((a): a is PendingAction => a != null);
    if (actions.length > 0) setPending(actions);
  };
  const runOne = (idx: number) => {
    const a = rowActions[idx];
    if (a) setPending([a]);
  };

  return (
    <div className="space-y-4">
      {readOnly && (
        <div className="flex items-center gap-2 rounded-md border border-slate-300 bg-slate-50 p-3 text-sm text-slate-700">
          <Lock className="h-4 w-4 shrink-0" />
          Режим просмотра: действия (создание СФ, пометка на удаление) отключены на сервере.
          Сверка работает только на чтение из 1С — ничего не меняется.
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 text-sm">
        <span className="text-muted-foreground">
          Всего расхождений: <b>{result.stats.total}</b> · нет СФ у получателя:{" "}
          <b>{result.stats.missing_sf_in_receiver}</b> · лишних СФ у передатчика:{" "}
          <b>{result.stats.extra_sf_in_sender}</b> · ПТМО без Поступления:{" "}
          <b>{result.stats.ptmo_without_postuplenie}</b>
        </span>
        <span className="ml-auto" />
        {!readOnly && (
          <>
            <Button
              disabled={selected.size === 0}
              onClick={runSelected}
              title="Выполнить выбранные действия в 1С"
            >
              Применить выбранные ({selected.size})
            </Button>
            <Button
              variant="outline"
              disabled={actionableIdx.length === 0}
              onClick={() => {
                setSelected(new Set(actionableIdx));
                const actions = actionableIdx
                  .map((i) => rowActions[i])
                  .filter((a): a is PendingAction => a != null);
                if (actions.length > 0) setPending(actions);
              }}
            >
              Применить ко всем ({actionableIdx.length})
            </Button>
          </>
        )}
      </div>

      {result.warnings.length > 0 && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
          {result.warnings.map((w, i) => (
            <p key={i}>{w}</p>
          ))}
        </div>
      )}

      {result.rows.length === 0 ? (
        <div className="rounded-md border border-dashed p-10 text-center text-sm text-muted-foreground">
          Расхождений не найдено — всё сошлось.
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                {!readOnly && (
                  <Checkbox
                    checked={allSelected}
                    onCheckedChange={toggleAll}
                    aria-label="Выбрать все"
                    disabled={actionableIdx.length === 0}
                  />
                )}
              </TableHead>
              <TableHead>Тип</TableHead>
              <TableHead>ПТМО</TableHead>
              <TableHead>СФ выданный (вх. №)</TableHead>
              <TableHead>Поступление</TableHead>
              <TableHead>СФ полученный</TableHead>
              <TableHead>Сумма</TableHead>
              <TableHead>Контрагент</TableHead>
              <TableHead className="min-w-[16rem]">Комментарий / статус</TableHead>
              <TableHead className="w-44">Действие</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {result.rows.map((row, idx) => {
              const meta = KIND_LABEL[row.kind];
              const st = statuses[idx];
              const action = rowActions[idx];
              const isDone = st?.phase === "done";
              const actionDisabledByCaps =
                row.available_action !== null && !actionAllowed(row.available_action, caps);
              return (
                <TableRow key={idx} className={isDone ? "opacity-60" : ""}>
                  <TableCell>
                    {action ? (
                      <Checkbox
                        checked={selected.has(idx)}
                        onCheckedChange={() => toggleOne(idx)}
                        disabled={isDone}
                        aria-label="Выбрать строку"
                      />
                    ) : null}
                  </TableCell>
                  <TableCell>
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${meta.cls}`}>
                      {meta.label}
                    </span>
                    {row.is_corrective && (
                      <span className="ml-1 inline-flex items-center rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-800">
                        корректировочный
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs">
                    {row.ptmo_number ?? "—"}
                    {row.ptmo_date ? <div className="text-muted-foreground">{formatRuDate(row.ptmo_date)}</div> : null}
                  </TableCell>
                  <TableCell className="text-xs">
                    {row.sf_issued_number ?? "—"}
                    {row.sf_issued_date ? <div className="text-muted-foreground">{formatRuDate(row.sf_issued_date)}</div> : null}
                  </TableCell>
                  <TableCell className="text-xs">
                    {row.postuplenie_number ?? "—"}
                    {row.postuplenie_date ? <div className="text-muted-foreground">{formatRuDate(row.postuplenie_date)}</div> : null}
                  </TableCell>
                  <TableCell className="text-xs">
                    {row.sf_received_number ?? "—"}
                    {row.sf_received_date ? <div className="text-muted-foreground">{formatRuDate(row.sf_received_date)}</div> : null}
                  </TableCell>
                  <TableCell className="text-xs whitespace-nowrap">{formatRub(row.ptmo_sum)}</TableCell>
                  <TableCell className="text-xs">{row.kontragent_name ?? "—"}</TableCell>
                  <TableCell className="text-xs">
                    {st ? (
                      <span className={st.phase === "error" ? "text-rose-700" : "text-emerald-700"}>
                        {st.phase === "error" ? (
                          <XCircle className="mr-1 inline h-3.5 w-3.5" />
                        ) : (
                          <CheckCircle2 className="mr-1 inline h-3.5 w-3.5" />
                        )}
                        {st.message}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">{row.note ?? ""}</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <ActionCell
                      action={action}
                      kind={row.available_action}
                      isDone={isDone}
                      disabledByCaps={actionDisabledByCaps}
                      onRun={() => runOne(idx)}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      {pending && (
        <ApproveBatchDialog
          pairId={pairId}
          actions={pending}
          onResult={handleResult}
          onClose={() => setPending(null)}
        />
      )}
    </div>
  );
}
